import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, Paper, Grid, Card, CardContent, CardMedia, Chip, CircularProgress, Alert } from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';

interface BoxListing {
  id: string;
  title: string;
  brand: string;
  boxType: string;
  year: number;
  sport: 'basketball' | 'football' | 'baseball' | 'hockey' | 'soccer';
  price: number;
  originalPrice?: number;
  description: string;
  imageUrl: string;
  additionalImageUrls?: string[];
  isPosted: boolean;
  isHomepage: boolean;
  condition: string;
  quantity: number;
  createdAt: string;
  updatedAt: string;
}

const BoxesPage: React.FC = () => {
  const { mode } = useTheme();
  const [boxes, setBoxes] = useState<BoxListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBoxes();
  }, []);

  const loadBoxes = () => {
    try {
      setLoading(true);
      const savedBoxes = localStorage.getItem('adminBoxes');
      if (savedBoxes) {
        const allBoxes = JSON.parse(savedBoxes);
        // Filter for boxes marked as posted
        const postedBoxes = allBoxes.filter((box: BoxListing) => box.isPosted);
        setBoxes(postedBoxes);
      } else {
        setBoxes([]);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load boxes');
      setBoxes([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 6, maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
          Sports Card Boxes
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Premium unopened hobby boxes for collectors
        </Typography>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Boxes Grid */}
      {boxes.length > 0 ? (
        <Grid container spacing={4}>
          {boxes.map((box) => (
            <Grid item xs={12} sm={6} md={4} key={box.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="250"
                  image={box.imageUrl || '/placeholder-box.jpg'}
                  alt={box.title}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = '/placeholder-box.jpg';
                  }}
                  sx={{ 
                    objectFit: 'contain',
                    backgroundColor: mode === 'light' ? '#f8f9fa' : '#2d3748',
                    padding: '12px'
                  }}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {box.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {box.brand} • {box.boxType} • {box.year} • {box.sport}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {formatPrice(box.price)}
                    </Typography>
                    {box.originalPrice && box.originalPrice > box.price && (
                      <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                        {formatPrice(box.originalPrice)}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={box.condition} 
                      size="small" 
                      sx={{ mr: 1, mb: 1 }}
                    />
                    {box.quantity > 0 && (
                      <Chip 
                        label={`${box.quantity} in stock`} 
                        size="small" 
                        color="success" 
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                    {box.description.length > 120 
                      ? `${box.description.substring(0, 120)}...` 
                      : box.description
                    }
                  </Typography>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 'auto',
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Paper 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              maxWidth: 500,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
              Coming Soon
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              We're working on bringing you the best selection of sports card hobby boxes. 
              Stay tuned for updates!
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Notify Me When Available
            </Button>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default BoxesPage; 